import type { AttachmentBlock } from '../../../contracts/harness.types';
import type { LinkedContextItem } from '../../../contracts/projects/linked-context';
import type { WorkReferencedObjectType } from '../../../contracts/work-launch-analytics.types';

/**
 * Classify prompt inputs into the warehouse referenced-object vocabulary.
 * Omit types we cannot classify honestly (e.g. library work_output artefacts).
 */
export function buildWorkReferencedObjectProps(input: {
  linkedItems?: readonly LinkedContextItem[] | null;
  optimisticAttachments?: readonly AttachmentBlock[] | null;
  attachmentCount?: number;
  routineName?: string | null;
}): {
  referenced_object_types?: WorkReferencedObjectType[];
  referenced_object_count?: number;
} {
  const types = new Set<WorkReferencedObjectType>();
  let count = 0;

  for (const item of input.linkedItems ?? []) {
    if (item.type === 'session') {
      types.add('session');
      count += 1;
      continue;
    }
    if (item.type === 'patient') {
      types.add('patient');
      count += 1;
      continue;
    }
    if (item.type === 'library_document') {
      if (item.documentKind === 'input') {
        types.add('file');
        count += 1;
      } else if (item.documentKind === 'workflow_context') {
        types.add('source');
        count += 1;
      }
      // documentKind === 'artifact' is a work output — not in the enum; omit.
    }
  }

  const fileAttachments = input.optimisticAttachments?.length
    ? input.optimisticAttachments.length
    : input.attachmentCount != null && input.attachmentCount > 0
      ? input.attachmentCount
      : 0;
  if (fileAttachments > 0) {
    types.add('file');
    count += fileAttachments;
  }

  if (input.routineName?.trim()) {
    types.add('routine');
    count += 1;
  }

  if (count === 0) return {};

  return {
    referenced_object_types: Array.from(types),
    referenced_object_count: count,
  };
}

/** Skills actually attached on the turn (computer-use / slash skills), not UI chrome. */
export function resolveWorkSkillCount(input: {
  computerUseSkill?: unknown;
  draftSkillCount?: number;
}): number | undefined {
  const fromDraft =
    typeof input.draftSkillCount === 'number' && input.draftSkillCount > 0
      ? input.draftSkillCount
      : 0;
  const fromComputerUse = input.computerUseSkill ? 1 : 0;
  const total = Math.max(fromDraft, fromComputerUse);
  return total > 0 ? total : undefined;
}
